import React, { useMemo, useState } from 'react';
import { Box, Button, Collapse, TablePagination, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { Impact, Severity } from '../../../api/ontologyChangeSetApi';
import { formatValue } from '../ontologyFormat';
import { SEVERITY_COLOR, formatTimestamp } from './changeSetReview';
import { MONO_SX, Notice, Pill, Section } from './changeSetUi';
import { dsVar } from '../../../theme/tokens';

const SEVERITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 };
const SAMPLE_PAGE = 10;

const CELL_SX = { px: 1.25, py: 0.75, fontSize: '0.75rem', color: dsVar('fg-soft'), verticalAlign: 'top', borderBottom: `1px solid ${dsVar('line')}` };
const HEAD_SX = { ...CELL_SX, color: dsVar('fg-muted'), fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.625rem', textAlign: 'left' };

const thresholdText = (side?: Record<string, any> | null) =>
    (side ? [side.operator, formatValue(side.value, side.unit)].filter(Boolean).join(' ') : 'none');

/** "17 customers' current decisions would be affected (17 medium, 3 high)". */
export const runtimeSentence = (impact?: Impact, verb = 'would be'): string => {
    const summary = impact?.runtime?.summary;
    const customers = summary?.customers ?? 0;
    const bySeverity = summary?.bySeverity ?? {};
    const parts = (['high', 'medium', 'low'] as Severity[])
        .filter((s) => (bySeverity[s] ?? 0) > 0)
        .map((s) => `${bySeverity[s]} ${s}`);
    const who = customers === 1 ? '1 customer\'s current decisions' : `${customers} customers' current decisions`;
    return `${who} ${verb} affected${parts.length ? ` (${parts.join(', ')})` : ''}.`;
};

export interface ChangeSetImpactPanelProps {
    impact?: Impact;
    title?: string;
    /** "would be" before a decision, "were" for what was actually flagged. */
    verb?: string;
    onRefresh?: () => void;
    refreshing?: boolean;
}

/**
 * The blast radius, shown above the items because it is what the reviewer is weighing
 * (spec §4.5). Computed when the change set was proposed, assuming every item is accepted.
 */
const ChangeSetImpactPanel: React.FC<ChangeSetImpactPanelProps> = ({
    impact, title = 'Impact', verb = 'would be', onRefresh, refreshing,
}) => {
    const [showActivities, setShowActivities] = useState(false);
    const [samplePage, setSamplePage] = useState(0);

    const activities = impact?.designTime?.activities ?? [];
    const flows = new Set(activities.flatMap((a) => a.flows ?? []));
    const deltas = impact?.parameterDeltas ?? [];
    const findings = impact?.regulatoryFindings ?? [];
    const runtime = impact?.runtime;

    const sample = useMemo(
        () => [...(runtime?.sample ?? [])].sort((a, b) =>
            (SEVERITY_RANK[a.severity ?? 'low'] ?? 3) - (SEVERITY_RANK[b.severity ?? 'low'] ?? 3)),
        [runtime?.sample],
    );

    const action = onRefresh ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {impact?.computedAt && (
                <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>Computed {formatTimestamp(impact.computedAt)}</Typography>
            )}
            <Button size="small" onClick={onRefresh} disabled={refreshing} startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: 'none', color: dsVar('fg-2'), fontSize: '0.75rem' }}>
                {refreshing ? 'Refreshing…' : 'Refresh impact'}
            </Button>
        </Box>
    ) : undefined;

    if (!impact) {
        return (
            <Section title={title} action={action}>
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>No impact has been computed.</Typography>
            </Section>
        );
    }

    return (
        <Section title={title} action={action}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {/* Runtime: the decisions already made that this would change */}
                {runtime?.unavailable ? (
                    <Notice tone="warning">Runtime impact unavailable: the context graph could not be reached.</Notice>
                ) : (
                    <Box>
                        <Typography sx={{ fontSize: '0.9375rem', color: dsVar('fg'), fontWeight: 500 }}>
                            {runtimeSentence(impact, verb)}
                        </Typography>
                        {runtime?.note && (
                            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mt: 0.25 }}>{runtime.note}</Typography>
                        )}
                    </Box>
                )}

                {findings.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {findings.map((finding, index) => (
                            <Notice key={index} tone={finding.breachesConstraint ? 'error' : 'warning'}>{finding.message}</Notice>
                        ))}
                    </Box>
                )}

                {/* Design time: which agent activities read a changed element */}
                {activities.length > 0 && (
                    <Box>
                        <Button
                            size="small"
                            onClick={() => setShowActivities((v) => !v)}
                            endIcon={<ExpandMoreIcon sx={{ transform: showActivities ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
                            sx={{ textTransform: 'none', color: dsVar('fg-soft'), px: 0 }}
                        >
                            Affects {activities.length} {activities.length === 1 ? 'activity' : 'activities'} in {flows.size} {flows.size === 1 ? 'workflow' : 'workflows'}
                        </Button>
                        <Collapse in={showActivities}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                                {activities.map((activity) => (
                                    <Box key={activity.activityTemplateCode} sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft') }}>
                                        {activity.activityTemplateName || activity.activityTemplateCode}
                                        <Box component="span" sx={{ color: dsVar('fg-muted'), ml: 1 }}>
                                            via {[...(activity.viaConcepts ?? []), ...(activity.viaParameters ?? [])].join(', ') || '—'}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Collapse>
                    </Box>
                )}

                {/* Threshold moves, per activity */}
                {deltas.length > 0 && (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                            <Box component="thead"><Box component="tr">
                                {['Threshold', 'Activity', 'Before → after'].map((label) => <Box key={label} component="th" sx={HEAD_SX}>{label}</Box>)}
                            </Box></Box>
                            <Box component="tbody">
                                {deltas.map((delta, index) => (
                                    <Box key={`${delta.parameterCode}-${delta.activityTemplateCode}-${index}`} component="tr">
                                        <Box component="td" sx={{ ...CELL_SX, ...MONO_SX }}>{delta.parameterCode}</Box>
                                        <Box component="td" sx={CELL_SX}>{delta.activityTemplateCode}</Box>
                                        <Box component="td" sx={CELL_SX}>
                                            {thresholdText(delta.before)} → <Box component="span" sx={{ color: dsVar('accent-fg') }}>{thresholdText(delta.after)}</Box>
                                            {delta.effective === false && (
                                                <Box sx={{ color: dsVar('fg-muted') }}>No effect here (overridden by {delta.shadowedBy})</Box>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* The decisions themselves, worst first */}
                {sample.length > 0 && (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                            <Box component="thead"><Box component="tr">
                                {['Customer', 'Severity', 'Activity', 'Why'].map((label) => <Box key={label} component="th" sx={HEAD_SX}>{label}</Box>)}
                            </Box></Box>
                            <Box component="tbody">
                                {sample.slice(samplePage * SAMPLE_PAGE, (samplePage + 1) * SAMPLE_PAGE).map((row, index) => (
                                    <Box key={`${row.sessionId}-${row.customerId}-${index}`} component="tr">
                                        <Box component="td" sx={{ ...CELL_SX, ...MONO_SX }}>{row.customerId}</Box>
                                        <Box component="td" sx={CELL_SX}>
                                            {row.severity && <Pill color={SEVERITY_COLOR[row.severity] ?? '#94A3B8'}>{row.severity}</Pill>}
                                        </Box>
                                        <Box component="td" sx={CELL_SX}>{row.activityCode}</Box>
                                        <Box component="td" sx={CELL_SX}>{(row.reasons ?? []).join('; ')}</Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        {sample.length > SAMPLE_PAGE && (
                            <TablePagination
                                component="div"
                                count={sample.length}
                                page={samplePage}
                                rowsPerPage={SAMPLE_PAGE}
                                rowsPerPageOptions={[SAMPLE_PAGE]}
                                onPageChange={(_e, next) => setSamplePage(next)}
                                sx={{ color: dsVar('fg-2'), '& .MuiIconButton-root': { color: dsVar('fg-2') } }}
                            />
                        )}
                    </Box>
                )}
            </Box>
        </Section>
    );
};

export default ChangeSetImpactPanel;
